import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { ThreeMFLoader } from 'three/addons/loaders/3MFLoader.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import * as TWEEN from "@tweenjs/tween.js";

// Most 3D printing software assumes Z-UP coordinate system.
THREE.Object3D.DEFAULT_UP = new THREE.Vector3(0, 0, 1);

export { ModelViewer };

class ModelViewer {
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.Camera;
    controls: OrbitControls;

    progressBar: HTMLProgressElement;

    currentModel: THREE.Object3D | null;

    static #initialCameraPosition = new THREE.Vector3(13, 13, 13);
    static #cameraResetTime = 1000; // ms

    #modelLoaders: Map<string, ModelLoader>;
    #tweens: TWEEN.Group;

    constructor(viewerContainer: HTMLElement) {
        const aspectRatio = 1;
        const whiteColor = new THREE.Color(0xffffff);
        const backgroundColor = new THREE.Color(
            window.getComputedStyle(document.body).backgroundColor
        );

        let canvas: HTMLCanvasElement = viewerContainer.querySelector('canvas')!;

        let { width } = canvas.getBoundingClientRect();
        canvas.width = width;
        canvas.height = width * aspectRatio;

        let scene = new THREE.Scene();
        scene.background = backgroundColor;

        let grid = new THREE.GridHelper(20, 20);
        grid.rotateX(0.5 * Math.PI);
        scene.add(grid);

        let ambientLight = new THREE.AmbientLight(whiteColor, 0.5);
        scene.add(ambientLight);

        let directionalLight = new THREE.DirectionalLight(whiteColor, 1.5);
        directionalLight.position.set(7, 7, 7);
        directionalLight.lookAt(new THREE.Vector3());
        scene.add(directionalLight);

        let camera = new THREE.PerspectiveCamera(75, canvas.width / canvas.height, 0.1, 1000);
        camera.position.copy(ModelViewer.#initialCameraPosition);

        let renderer = new THREE.WebGLRenderer({
            canvas: canvas,
            antialias: true,
        });
        renderer.setAnimationLoop(this.#render.bind(this));

        let controls = new OrbitControls(camera, renderer.domElement);
        controls.enablePan = false;
        controls.enableZoom = false;

        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this.controls = controls;

        this.progressBar = viewerContainer.querySelector("progress")!;

        this.currentModel = null;
        this.#modelLoaders = new Map<string, ModelLoader>([
            ["stl", new STLModelLoader(this.#onProgress.bind(this))],
            ["3mf", new ThreeMFModelLoader(this.#onProgress.bind(this))],
            ["obj", new OBJModelLoader(this.#onProgress.bind(this))],
        ]);
        this.#tweens = new TWEEN.Group();
    }

    #onProgress(progress: ProgressEvent) {
        this.progressBar.value = progress.loaded;
        this.progressBar.max = progress.total;
        let percentage = (progress.loaded / progress.total * 100).toFixed(2);
        this.progressBar.textContent = `${percentage}%`;
    }

    #render(dt: number) {
        this.#tweens.update(dt);
        this.controls.update(dt);
        this.renderer.render(this.scene, this.camera);
    }

    #centerAndScaleModel(model: THREE.Object3D) {
        const maxExtent = 20;

        let bbox = new THREE.Box3().setFromObject(model);
        let size = bbox.getSize(new THREE.Vector3());
        let largestDimension = Math.max(size.x, size.y, size.z);

        let scale = maxExtent / largestDimension;
        model.scale.set(scale, scale, scale);
        bbox.setFromObject(model);
        bbox.getSize(size);

        let center = bbox.getCenter(new THREE.Vector3());
        let offset = center.sub(new THREE.Vector3(0, 0, size.z / 2)).negate();
        model.position.copy(offset);
    }

    #resetCameraPositionSmoothly(model: THREE.Object3D) {
        let bbox = new THREE.Box3().setFromObject(model);
        let center = bbox.getCenter(new THREE.Vector3());

        let cameraTween = new TWEEN.Tween(this.camera.position)
            .easing(TWEEN.Easing.Quadratic.InOut)
            .to(ModelViewer.#initialCameraPosition, ModelViewer.#cameraResetTime)
            .start();
        let controlsTween = new TWEEN.Tween(this.controls.target)
            .easing(TWEEN.Easing.Quadratic.InOut)
            .to(center, ModelViewer.#cameraResetTime)
            .start();

        this.#tweens.removeAll();
        this.#tweens.add(cameraTween, controlsTween);
    }

    #replaceModel(model: THREE.Object3D) {
        this.clear();

        this.#centerAndScaleModel(model);
        this.#resetCameraPositionSmoothly(model);

        this.currentModel = model;
        this.scene.add(model);
    }

    async loadModel(file: File) {
        let separatorIdx = file.name.lastIndexOf(".");
        let fileExtension = file.name.substring(separatorIdx + 1).toLowerCase();

        let modelLoader = this.#modelLoaders.get(fileExtension);
        if (!modelLoader)
            throw new Error("Unsupported file format");

        this.progressBar.style.display = 'initial';

        try {
            let fileUrl = URL.createObjectURL(file);
            let model = await modelLoader.loadModel(fileUrl);
            this.#replaceModel(model);
        } finally {
            this.progressBar.style.display = 'none';
        }
    }

    clear() {
        if (this.currentModel)
            this.scene.remove(this.currentModel);
    }

    supportedFormats(): string[] {
        return [...this.#modelLoaders.keys()];
    }
}

type OnProgress = (event: ProgressEvent) => void;

abstract class ModelLoader {
    onProgress: OnProgress;

    constructor(onProgress: OnProgress) {
        this.onProgress = onProgress;
    }

    abstract loadModel(url: string): Promise<THREE.Object3D>;
}

class STLModelLoader extends ModelLoader {
    loader = new STLLoader();

    async loadModel(url: string): Promise<THREE.Object3D> {
        let geometry = await this.loader.loadAsync(url, this.onProgress);
        return new THREE.Mesh(geometry, new THREE.MeshPhongMaterial({ flatShading: true }));
    }
}

class ThreeMFModelLoader extends ModelLoader {
    loader = new ThreeMFLoader();

    loadModel(url: string): Promise<THREE.Object3D> {
        return this.loader.loadAsync(url, this.onProgress);
    }
}

class OBJModelLoader extends ModelLoader {
    loader = new OBJLoader();

    loadModel(url: string): Promise<THREE.Object3D> {
        return this.loader.loadAsync(url, this.onProgress);
    }
}
